import React, { useEffect, useState } from "react";
import { BarChart3, TrendingUp, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";

function pct(num, den) {
    if (!den) return "0%";
    return `${Math.round((num / den) * 100)}%`;
}

function StatsTable({ title, rows }) {
    return (
        <div className="bg-white border border-magni-light overflow-hidden" data-testid={`utm-table-${title.toLowerCase()}`}>
            <div className="px-6 py-4 border-b border-magni-light bg-magni-cream">
                <h3 className="font-serif-display text-xl text-magni-navy">{title}</h3>
            </div>
            {rows.length === 0 ? (
                <div className="px-6 py-10 text-center text-sm text-magni-muted">
                    Nessun dato disponibile per questo filtro.
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-[0.12em] text-magni-muted border-b border-magni-light">
                                <th className="px-6 py-3 font-semibold">Valore</th>
                                <th className="px-4 py-3 font-semibold text-right">Prenotazioni</th>
                                <th className="px-4 py-3 font-semibold text-right">Check-in</th>
                                <th className="px-4 py-3 font-semibold text-right">Conversion</th>
                                <th className="px-4 py-3 font-semibold text-right">Acquisti</th>
                                <th className="px-4 py-3 font-semibold text-right">Tasso acquisto</th>
                                <th className="px-4 py-3 font-semibold text-right">Annullate</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.value} className="border-b border-magni-light/60 hover:bg-magni-cream/40">
                                    <td className="px-6 py-3 text-magni-navy font-semibold">{r.value || "(diretto)"}</td>
                                    <td className="px-4 py-3 text-right">{r.count}</td>
                                    <td className="px-4 py-3 text-right">{r.checked_in}</td>
                                    <td className="px-4 py-3 text-right">{pct(r.checked_in, r.count)}</td>
                                    <td className="px-4 py-3 text-right">{r.purchased}</td>
                                    <td className="px-4 py-3 text-right">{pct(r.purchased, r.checked_in)}</td>
                                    <td className="px-4 py-3 text-right text-magni-muted">{r.cancelled}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default function AdminCampaigns() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [filters, setFilters] = useState({ date_from: "", date_to: "" });

    const refresh = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.date_from) params.date_from = filters.date_from;
            if (filters.date_to) params.date_to = filters.date_to;
            const r = await api.get("/admin/utm-stats", { params });
            setData(r.data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.date_from, filters.date_to]);

    return (
        <div className="space-y-8" data-testid="admin-campaigns-page">
            <Toaster position="top-center" richColors />

            <div>
                <div className="flex items-center gap-3 mb-2">
                    <BarChart3 className="w-5 h-5 text-magni-navy" />
                    <span className="overline">Tracking · Marketing</span>
                </div>
                <h1 className="font-serif-display text-3xl sm:text-4xl text-magni-navy">
                    Campagne UTM
                </h1>
                <p className="text-magni-muted text-sm mt-2 max-w-2xl">
                    Misura quanti clienti arrivano da ciascuna campagna pubblicitaria. Aggiungi i parametri{" "}
                    <code className="text-magni-navy bg-magni-cream px-1">?utm_source=meta&amp;utm_campaign=primavera</code>{" "}
                    ai link delle tue pubblicità: vengono salvati con ogni prenotazione.
                </p>
            </div>

            {/* Filters */}
            <div className="bg-white border border-magni-light p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                    <label className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy block mb-2">
                        Dal
                    </label>
                    <input
                        type="date"
                        data-testid="utm-filter-from"
                        value={filters.date_from}
                        onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                        className="magni-input"
                    />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy block mb-2">
                        Al
                    </label>
                    <input
                        type="date"
                        data-testid="utm-filter-to"
                        value={filters.date_to}
                        onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                        className="magni-input"
                    />
                </div>
                <div className="flex items-end">
                    <button
                        onClick={() => setFilters({ date_from: "", date_to: "" })}
                        className="magni-btn-secondary text-xs"
                        data-testid="utm-filter-reset"
                    >
                        Resetta filtri
                    </button>
                </div>
            </div>

            {loading && (
                <div className="flex items-center justify-center py-16 text-magni-muted">
                    <Loader2 className="w-6 h-6 animate-spin mr-2" />
                    Caricamento dati...
                </div>
            )}

            {!loading && data && (
                <>
                    <div className="bg-white border border-magni-light p-6 flex items-center gap-4">
                        <TrendingUp className="w-6 h-6 text-magni-navy" />
                        <div>
                            <div className="overline">Prenotazioni nel periodo</div>
                            <div className="font-serif-display text-3xl text-magni-navy" data-testid="utm-total">
                                {data.total}
                            </div>
                        </div>
                    </div>

                    <StatsTable title="Source" rows={data.by_source} />
                    <StatsTable title="Medium" rows={data.by_medium} />
                    <StatsTable title="Campaign" rows={data.by_campaign} />
                </>
            )}
        </div>
    );
}
